import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import { Reducer } from 'redux';

import BigNumber from 'bignumber.js';
import {
  isValidDecimal,
  parseFormattedFloat,
} from '@paradigm/utils/formatDecimal';
import { Maybe } from '@paradigm/utils/types';
import {
  FutureStrategyLeg,
  OptionStrategyLeg,
  STRATEGIES,
} from '#/shared/strategies';
import {
  getDefaultFutureProductCode,
  getDefaultProductCode,
  getProduct,
  findHedgeProduct,
} from '#/products/domain';
import { PRODUCTS } from '#/products/config';
import { isProductDisabledForType, Product } from '#/products/types';
import { GrfqVenue } from '#/unified-rfqs/entities/products/venue';
import { Counterparty } from '#/unified-rfqs/entities/drfq/domain';
import { findBaseLegIndex } from '#/unified-rfqs/utils/legs';

import { DefaultAccountsRecord } from '#/preferences/default-accounts/types';
import { AuctionType } from '#/unified-rfqs/entities/shared';
import { UnifiedInstruments } from '#/unified-rfqs/ui/instruments/types';
import {
  DraftFutureLeg,
  DraftLeg,
  DraftOptionLeg,
  DraftUncategorizedLeg,
} from './types';
import {
  checkStrategyLegExpirationPolicy,
  checkStrategyLegStrikePolicy,
} from './leg-validation';

import { isFutureLeg, isHedgeLeg, isOptionLeg } from './leg-functions';
import { isMultiProductAllowed, rfqProductCodes } from './multi-product';
import calculateLegRatio, { calculateStrategyQty } from './calculateLegRatio';
import calculateGreeks from './calculateGreeks';
import calculateHedgeLegQuantity from './calculateHedgeLegQuantity';

type Defined<T> = T extends undefined ? never : T;

export interface State {
  readonly venue: GrfqVenue['code'];
  /** `null` means all products */
  readonly productCode: Maybe<string>;
  /**
   * Whether the user explicitly entered multi-product mode (the "Multi"
   * market option / shortcut). Only affects presentation: the market
   * selector keeps showing "Multi" while the legs are being filled in,
   * even when they momentarily resolve to a single product.
   */
  readonly isMultiProduct: boolean;
  readonly auction: AuctionType | null;
  /** `null` means custom strategy */
  readonly strategyCode: string | null | undefined;
  readonly legs: readonly DraftLeg[];
  readonly counterparties: readonly Counterparty[];
  readonly anonymous: boolean;
  /** `null` means it's a grfq */
  readonly account: string | null;
  readonly instruments: UnifiedInstruments;
  readonly hiddenLimitOrder: {
    readonly enabled: boolean;
    readonly bidPrice: string;
    readonly askPrice: string;
  };
}

interface OptionLegStrikeSelectionPayload {
  readonly strike: Defined<DraftOptionLeg['strike']>;
  readonly legIndex: number;
}

interface OptionLegExpirationSelectionPayload {
  readonly expiration: Defined<DraftLeg['expiration']>;
  readonly legIndex: number;
}

interface FutureLegExpirationSelectionPayload {
  readonly expiration: Defined<DraftFutureLeg['expiration']>;
  readonly legIndex: number;
}

interface LegQuantitySelectionPayload {
  readonly legIndex: number;
  readonly quantity: string;
}

interface LegProductSelectionPayload {
  readonly productCode: Defined<DraftLeg['productCode']>;
  readonly legIndex: number;
  readonly defaultAccounts: Maybe<DefaultAccountsRecord>;
  /**
   * Venues allowed to hold legs on more than one product
   * (`feature_um_multi_product_rfq` variant attachment).
   */
  readonly multiProductVenues: readonly string[];
}

interface OptionLegInstrumentSelectionPayload {
  readonly optionKind: Defined<DraftOptionLeg['optionKind']>;
  readonly legIndex: number;
}

interface LegPriceChangePayload {
  readonly price: Defined<DraftFutureLeg['price']>;
  readonly legIndex: number;
}

interface LegRatioChangePayload {
  readonly ratio: Defined<DraftLeg['ratio']>;
  readonly legIndex: number;
}

export const INITIAL_STATE: State = {
  venue: 'DBT',
  productCode: 'DO',
  isMultiProduct: false,
  auction: 'RFQ',
  account: null,
  anonymous: false,
  strategyCode: undefined,
  legs: [],
  counterparties: [],
  instruments: {
    futureInstruments: [],
    optionInstruments: [],
  },
  hiddenLimitOrder: {
    enabled: false,
    bidPrice: '',
    askPrice: '',
  },
};

const slice = createSlice({
  name: 'rfq-builder',
  initialState: INITIAL_STATE,
  reducers: {
    venueSelection: (state, action: PayloadAction<UpdateVenuePayload>) => {
      const { venue, defaultAccounts } = action.payload;
      state.venue = venue;
      state.isMultiProduct = false;
      state.strategyCode = undefined;
      const productCode = getDefaultProductCode(state.venue) ?? null;
      state.productCode = productCode;
      state.account = getDefaultAccount(state, {
        venue,
        productCode,
        defaultAccounts,
      });
      state.legs = [];
    },

    productSelection: (state, action: PayloadAction<UpdateProductPayload>) => {
      const { productCode, defaultAccounts } = action.payload;
      state.productCode = productCode;
      state.isMultiProduct = false;
      state.legs = [];
      if (productCode == null) {
        state.strategyCode = null;
        return;
      }

      state.strategyCode = undefined;
      const { venue } = getProduct(productCode);
      state.venue = venue;
      state.account = getDefaultAccount(state, {
        venue,
        productCode,
        defaultAccounts,
      });
    },

    accountSelection: (state, action: PayloadAction<State['account']>) => {
      state.account = action.payload;
    },

    auctionSelection: (state, action: PayloadAction<State['auction']>) => {
      const auction = action.payload;
      state.auction = auction;
      // Multi-product is an RFQ-only mode (isMultiProductAllowed), so the
      // order book drops back to the plain "All" presentation.
      if (auction === 'OB') state.isMultiProduct = false;
      // Reset state if the selected product is disabled for the new auction type
      if (
        state.productCode != null &&
        auction === 'OB' &&
        PRODUCTS[state.productCode] != null &&
        isProductDisabledForType(PRODUCTS[state.productCode]!, 'OB')
      ) {
        state.legs = [];
        state.strategyCode = undefined;
        state.productCode = 'DO';
      }
    },

    toggleAnonymous: (state) => {
      state.anonymous = !state.anonymous;
    },

    /**
     * Explicit multi-product entry (the "Multi" market option / shortcut):
     * a custom strategy with no structure product, seeded with two empty
     * legs so the next step -- picking a product per leg -- is in view.
     */
    multiProductSelection: (state) => {
      state.productCode = null;
      state.strategyCode = null;
      state.isMultiProduct = true;
      state.legs = [generateUncategorizedLeg(0), generateUncategorizedLeg(1)];
    },

    duplicateLeg: (state, action: PayloadAction<{ legIndex: number }>) => {
      const { legIndex } = action.payload;
      const leg = selectLeg(state, legIndex);
      const nextLegs = [...state.legs, leg];
      state.legs = nextLegs.map(updateIndex);

      applyCustomStrategy(state, state.productCode);
    },

    strategySelection: (
      state,
      action: PayloadAction<Defined<State['strategyCode']>>,
    ) => {
      const strategyCode = action.payload;
      if (strategyCode === null) customStrategySelection(state);
      else {
        // A preset generates single-product legs, so it exits multi-product
        // mode; changing a leg's product afterwards re-enters it.
        state.isMultiProduct = false;
        predefinedStrategySelection(state, strategyCode);
      }
    },

    optionLegExpirationSelection: (
      state,
      action: PayloadAction<OptionLegExpirationSelectionPayload>,
    ) => {
      if (state.strategyCode === null)
        updateSingleOptionLegExpiration(state, action.payload);
      else predefinedOptionLegExpirationSelection(state, action.payload);
    },

    futureLegExpirationSelection: (
      state,
      action: PayloadAction<FutureLegExpirationSelectionPayload>,
    ) => {
      if (state.strategyCode === null)
        updateSingleFutureLegExpiration(state, action.payload);
      else predefinedFutureLegExpirationSelection(state, action.payload);
    },

    optionLegStrikeSelection: (
      state,
      action: PayloadAction<OptionLegStrikeSelectionPayload>,
    ) => {
      if (state.strategyCode === null)
        updateSingleOptionLegStrike(state, action.payload);
      else predefinedOptionLegStrikeSelection(state, action.payload);
    },

    legQuantitySelection: (
      state,
      action: PayloadAction<LegQuantitySelectionPayload>,
    ) => {
      const { quantity, legIndex } = action.payload;
      const leg = selectLeg(state, legIndex);
      leg.quantity = quantity;

      if (!isCustomStrategy(state) && legIndex === 0) {
        updateLegsQuantityFromBaseLeg(state, quantity);
      }

      const matchedStrategyByRatio = matchStrategyByRatio(state);
      if (matchedStrategyByRatio == null) {
        applyCustomStrategy(state);
      }

      updateRfqDerivedRatio(state);
    },

    legProductSelection: (
      state,
      action: PayloadAction<LegProductSelectionPayload>,
    ) => {
      const allowMultiProduct = isMultiProductAllowed(
        action.payload.multiProductVenues,
        state.venue,
        state.auction,
      );
      if (allowMultiProduct) {
        multiProductLegProductSelection(state, action);
        return;
      }

      const { productCode, legIndex, defaultAccounts } = action.payload;
      const product = getProduct(productCode);
      const currentLeg = selectLeg(state, legIndex);
      const isSameProduct = isSameRfqProduct(state, productCode);
      state.productCode = productCode;
      if (!isSameProduct) {
        state.account = getDefaultAccount(state, {
          productCode,
          venue: product.venue,
          defaultAccounts,
        });
      }

      /**
       * When changing the kind (OPTION | FUTURE), the strategy changes, so we apply custom
       */
      if (currentLeg.kind !== product.kind) {
        applyCustomStrategy(state, productCode);
      }

      if (product.kind === 'FUTURE') futureLegProductSelection(state, action);
      if (product.kind === 'OPTION') optionLegProductSelection(state, action);
    },

    legActionToggle: (
      state,
      action: PayloadAction<{ readonly legIndex: number }>,
    ) => {
      const { legIndex } = action.payload;
      const leg = selectLeg(state, legIndex);
      leg.action = leg.action === 'BUY' ? 'SELL' : 'BUY';
      if (isPredefinedLeg(state, leg))
        applyCustomStrategy(state, state.productCode);
    },

    optionLegInstrumentSelection: (
      state,
      action: PayloadAction<OptionLegInstrumentSelectionPayload>,
    ) => {
      const { legIndex, optionKind } = action.payload;
      const leg = selectLeg(state, legIndex);
      assertIsOptionLeg(leg);
      const optionLegs = state.legs.filter(isOptionLeg);
      const isSingleLeg = optionLegs.length === 1;
      const isEveryOptionProductEqual = optionLegs.every(
        ({ productCode }) => productCode === leg.productCode,
      );
      const isSingleOptionProduct = isSingleLeg || isEveryOptionProductEqual;

      const newProductCode = isSingleOptionProduct ? state.productCode : null;
      leg.optionKind = optionKind;
      applyCustomStrategy(state, newProductCode);
    },

    legRatioChange: (state, action: PayloadAction<LegRatioChangePayload>) => {
      const { legIndex, ratio } = action.payload;
      const leg = selectLeg(state, legIndex);
      leg.ratio = ratio;
      if (isPredefinedLeg(state, leg))
        applyCustomStrategy(state, state.productCode);
    },

    legPriceChange: (state, action: PayloadAction<LegPriceChangePayload>) => {
      const { legIndex, price } = action.payload;
      const leg = selectLeg(state, legIndex);
      if (isPredefinedLeg(state, leg))
        throw new TypeError('Only hedge legs have price');

      assertIsFutureLeg(leg);

      leg.price = price;

      updateRfqDerivedRatio(state);
    },

    clearAllLegsRequest: (state) => {
      state.legs = [];
      if (!isCustomStrategy(state)) unsetStrategy(state);
    },

    newLegRequest: (state) => {
      const { productCode, legs } = state;
      const nextLegIndex = legs.length;
      const newLeg = generateUncategorizedLeg(nextLegIndex);
      legs.push(newLeg);
      applyCustomStrategy(state, productCode);
    },

    legRemovalRequest: (
      state,
      action: PayloadAction<{ readonly legIndex: number }>,
    ) => {
      const removedLeg = state.legs[action.payload.legIndex]!;
      const haveMoreThanOneOptionLeg =
        state.legs.filter(isOptionLeg).length > 1;
      state.legs = state.legs
        .filter((leg) => leg.index !== action.payload.legIndex)
        .map(updateIndex);

      if (removedLeg.isHedge || haveMoreThanOneOptionLeg) return;

      applyCustomStrategy(state, state.productCode);
    },

    addHedgeLeg: (
      state,
      action: PayloadAction<{
        readonly isDisabled: boolean;
      }>,
    ) => {
      if (action.payload.isDisabled) return;

      const optionLeg = state.legs.find(isOptionLeg);
      // If there is no option legs the button should return an error message
      assertIsOptionLeg(optionLeg!);

      const isPredefinedStrategy = !isCustomStrategy(state);

      if (isPredefinedStrategy) {
        assertStrategyCode(state.strategyCode);
        const strategy = STRATEGIES[state.strategyCode]!;

        if (strategy.kind !== 'OPTION')
          throw new TypeError(
            'Hedge is only supported for option strategies.' +
              ` The strategy currently selected is ${strategy.kind}.`,
          );

        if (state.productCode == null)
          throw new TypeError(
            'The productCode should be defined when a strategy is selected',
          );

        const numCurrentHedgeLegs = state.legs.filter(isHedgeLeg).length;
        const numStrategyHedgeLegs = strategy.hedgeLegs.length;

        /**
         * If user has added predefined strategy hedge legs
         * and tries to add more hedge, convert strategy to
         * custom
         */
        if (numCurrentHedgeLegs >= numStrategyHedgeLegs) {
          applyCustomStrategy(state, state.productCode);
        }
      }

      const hedgeLeg = generateHedgeLegFromOption(state, optionLeg);

      state.legs = [...state.legs, hedgeLeg];
    },

    counterpartiesSelection: (
      state,
      action: PayloadAction<readonly Counterparty[]>,
    ) => {
      return {
        ...state,
        counterparties: action.payload,
      };
    },
    updateInstruments: (state, action: PayloadAction<UnifiedInstruments>) => {
      return {
        ...state,
        instruments: action.payload,
      };
    },
    updateHedgeLeg: (state, action: PayloadAction<{ legIndex: number }>) => {
      const { legIndex } = action.payload;
      const isObAction = state.auction === 'OB';
      const baseLegIndex = findBaseLegIndex(state.legs);
      const baseLeg = state.legs[baseLegIndex];
      if (baseLeg == null || baseLeg.productCode == null) return state;

      const greeks = greeksSelection(state);
      const hedgeLeg = selectLeg(state, legIndex);

      if (isObAction) {
        const ratio = BigNumber(greeks.delta)
          .minus(greeks.mark_price ?? 0)
          .times(-1);

        return {
          ...state,
          legs: [
            ...state.legs.slice(0, legIndex),
            {
              ...hedgeLeg,
              action: hedgeLegActionBySign(ratio, hedgeLeg.action),
              ratio: ratio.abs().toString(),
            },
            ...state.legs.slice(legIndex + 1),
          ],
        };
      }

      const qtyBigNumber = calculateHedgeLegQuantity(
        hedgeLeg.productCode,
        baseLeg.productCode,
        hedgeLeg.price,
        greeks,
        hedgeLeg.expiration != null,
      );

      const updatedLeg = {
        ...hedgeLeg,
        quantity: qtyBigNumber?.absoluteValue().toString() ?? hedgeLeg.quantity,
        action: hedgeLegActionBySign(qtyBigNumber, hedgeLeg.action),
      };

      return {
        ...state,
        legs: [
          ...state.legs.slice(0, legIndex),
          {
            ...updatedLeg,
            ratio:
              calculateLegRatio(baseLeg.quantity ?? null, updatedLeg) ?? '',
          },
          ...state.legs.slice(legIndex + 1),
        ],
      };
    },

    hiddenLimitOrderToggle: (state, action: PayloadAction<boolean>) => {
      state.hiddenLimitOrder.enabled = action.payload;
      if (!action.payload) {
        state.hiddenLimitOrder.bidPrice = '';
        state.hiddenLimitOrder.askPrice = '';
      }
    },

    hiddenLimitOrderBidPriceChange: (state, action: PayloadAction<string>) => {
      state.hiddenLimitOrder.bidPrice = action.payload;
    },

    hiddenLimitOrderAskPriceChange: (state, action: PayloadAction<string>) => {
      state.hiddenLimitOrder.askPrice = action.payload;
    },
  },
});

export const { reducer } = slice as { reducer: Reducer<State> };
export const {
  venueSelection,
  productSelection,
  multiProductSelection,
  accountSelection,
  auctionSelection,
  strategySelection,
  futureLegExpirationSelection,
  optionLegExpirationSelection,
  optionLegStrikeSelection,
  legQuantitySelection,
  legProductSelection,
  legActionToggle,
  optionLegInstrumentSelection,
  legRatioChange,
  legPriceChange,
  clearAllLegsRequest,
  newLegRequest,
  legRemovalRequest,
  addHedgeLeg,
  counterpartiesSelection,
  toggleAnonymous,
  duplicateLeg,
  updateInstruments,
  updateHedgeLeg,
  hiddenLimitOrderToggle,
  hiddenLimitOrderBidPriceChange,
  hiddenLimitOrderAskPriceChange,
} = slice.actions;

function generateHedgeLegFromOption(
  state: Draft<State>,
  optionLeg: DraftOptionLeg,
): DraftFutureLeg {
  const hedgeProduct = findHedgeProduct({ for: optionLeg.productCode });
  const generateHedgeLeg = hedgeLegGenerator(hedgeProduct);

  return generateHedgeLeg({ ratio: 1 }, state.legs.length);
}

function isPredefinedLeg(state: Draft<State>, leg: DraftLeg) {
  return state.productCode != null && leg.productCode === state.productCode;
}

function updateIndex<T extends { readonly index: number }>(
  obj: T,
  newIndex: number,
) {
  return { ...obj, index: newIndex };
}

function unsetStrategy(state: Draft<State>) {
  state.strategyCode = undefined;
}

function applyCustomStrategy(
  state: Draft<State>,
  productCode: string | null = null,
) {
  state.strategyCode = null;
  state.productCode = productCode;
}

function isCustomStrategy(state: Draft<State>) {
  return state.strategyCode === null;
}

function predefinedOptionLegExpirationSelection(
  state: Draft<State>,
  payload: OptionLegExpirationSelectionPayload,
) {
  assertStrategyCode(state.strategyCode);

  const strategy = STRATEGIES[state.strategyCode]!;

  switch (strategy.expirationPolicy) {
    case 'independent':
      updateSingleOptionLegExpiration(state, payload);
      break;
    case 'mirror':
      updateAllOptionLegsExpiration(state, payload);
      break;
    // no default
  }

  if (strategy.kind !== 'OPTION') return;

  // Check for option strategy expiration policy validation
  const isLegExpirationPolicyValid = checkStrategyLegExpirationPolicy(
    strategy,
    state.legs.filter(isOptionLeg),
  );
  const shouldBecomeCustomStrategy = !isLegExpirationPolicyValid;

  if (shouldBecomeCustomStrategy) applyCustomStrategy(state, state.productCode);
}

function predefinedFutureLegExpirationSelection(
  state: Draft<State>,
  payload: FutureLegExpirationSelectionPayload,
) {
  assertStrategyCode(state.strategyCode);
  const strategy = STRATEGIES[state.strategyCode]!;

  switch (strategy.expirationPolicy) {
    case 'independent':
      updateSingleFutureLegExpiration(state, payload);
      break;
    case 'mirror':
      updateAllFutureLegsExpiration(state, payload);
      break;
    // no default
  }
}

function predefinedOptionLegStrikeSelection(
  state: Draft<State>,
  payload: OptionLegStrikeSelectionPayload,
) {
  assertStrategyCode(state.strategyCode);
  const strategy = STRATEGIES[state.strategyCode]!;
  const { productCode } = state;

  if (strategy.kind !== 'OPTION')
    throw new TypeError(`Only OPTION strategies have legs with strikes.`);

  switch (strategy.strikePolicy) {
    case 'independent':
      updateSingleOptionLegStrike(state, payload);
      break;
    case 'mirror':
      updateAllOptionLegsStrike(state, payload);
      break;
    // no default
  }

  const isLegStrikePolicyValid = checkStrategyLegStrikePolicy(
    strategy,
    state.legs.filter(isOptionLeg),
  );
  const shouldBecomeCustomStrategy = !isLegStrikePolicyValid;

  if (shouldBecomeCustomStrategy) applyCustomStrategy(state, productCode);
}

function futureLegGenerator(productCode: string) {
  return function generateFutureLeg(
    legTemplate: FutureStrategyLeg,
    index: number,
  ): DraftFutureLeg {
    return {
      index,
      productCode,
      action:
        legTemplate.ratio != null && legTemplate.ratio > 0 ? 'BUY' : 'SELL',
      expiration: undefined,
      ratio:
        legTemplate.ratio != null ? Math.abs(legTemplate.ratio).toString() : '',
      kind: 'FUTURE',
      quantity: '', // prevents builder from displaying stale leg quantity when user switches between strategies.
      isHedge: false,
    };
  };
}

export function hedgeLegGenerator(product: Product) {
  return function generateHedgeLeg(
    legTemplate: FutureStrategyLeg,
    index: number,
  ): DraftFutureLeg {
    return {
      index,
      productCode: product.code,
      action: legTemplate.action ?? 'BUY',
      expiration: null, // `null` represents 'Perpetual'
      ratio: legTemplate.ratio?.toString() ?? '',
      kind: 'FUTURE',
      price: legTemplate.price ?? null,
      quantity: legTemplate.quantity?.toString() ?? '',
      currency: product.currency,
      marginKind: product.marginType,
      isHedge: true,
    };
  };
}

function optionLegGenerator(productCode: string) {
  return function generateOptionLeg(
    legTemplate: OptionStrategyLeg,
    index: number,
  ): DraftOptionLeg {
    return {
      index,
      productCode,
      action:
        legTemplate.ratio != null && legTemplate.ratio > 0 ? 'BUY' : 'SELL',
      expiration: undefined,
      ratio:
        legTemplate.ratio != null ? Math.abs(legTemplate.ratio).toString() : '',
      kind: 'OPTION',
      optionKind: legTemplate.optionKind,
      strike: undefined,
      quantity: '', // prevents builder from displaying stale leg quantity when user switches between strategies.
      isHedge: false,
    };
  };
}

function generateUncategorizedLeg(index: number): DraftUncategorizedLeg {
  return {
    index,
    productCode: undefined,
    action: 'BUY',
    expiration: undefined,
    ratio: '1',
    kind: undefined,
    quantity: undefined,
    isHedge: false,
  };
}

interface UpdateSingleFutureLegExpirationPayload {
  readonly expiration: Defined<DraftFutureLeg['expiration']>;
  readonly legIndex: number;
}

function updateSingleFutureLegExpiration(
  state: Draft<State>,
  { expiration, legIndex }: UpdateSingleFutureLegExpirationPayload,
) {
  const leg = selectLeg(state, legIndex);
  assertIsFutureLeg(leg);
  leg.expiration = expiration;
}

interface UpdateAllFutureLegsExpirationPayload {
  readonly expiration: Defined<DraftFutureLeg['expiration']>;
}

function updateAllFutureLegsExpiration(
  state: Draft<State>,
  { expiration }: UpdateAllFutureLegsExpirationPayload,
) {
  state.legs.filter(isFutureLeg).forEach((leg) => {
    leg.expiration = expiration;
  });
}

interface UpdateSingleOptionLegExpirationPayload {
  readonly expiration: Defined<DraftLeg['expiration']>;
  readonly legIndex: number;
}

interface UpdateVenuePayload {
  readonly venue: State['venue'];
  readonly defaultAccounts: Maybe<DefaultAccountsRecord>;
}

interface UpdateProductPayload {
  readonly productCode: Maybe<string>;
  readonly defaultAccounts: Maybe<DefaultAccountsRecord>;
}

interface DefaultAccountOptions {
  readonly productCode: Maybe<string>;
  readonly venue: State['venue'];
  readonly defaultAccounts: Maybe<DefaultAccountsRecord>;
}

/**
 * When selecting a new future product leg:
 * - Change product code of existing legs
 * - Filter out hedge legs
 */
function futureLegProductSelection(
  state: Draft<State>,
  action: PayloadAction<LegProductSelectionPayload>,
) {
  const { productCode } = action.payload;
  const product = getProduct(productCode);
  if (product.kind !== 'FUTURE') {
    throw new TypeError('Product code should be of kind FUTURE.');
  }

  state.legs = state.legs
    .filter((leg) => !isHedgeLeg(leg))
    .map((leg, index) => {
      return {
        ...leg,
        index,
        productCode,
        kind: 'FUTURE',
        expiration:
          leg.expiration === undefined
            ? soleFutureExpiration(state, product, leg.isHedge)
            : leg.expiration,
      };
    });
}

/**
 * When changing to an option product:
 * - Change normal legs to new product
 *    - Tries to preserve expiry and ratio if compatible
 * - If has hedge legs:
 *    - Create corresponding hedge legs for the new option product code
 *       - Keep expiries if they are available and update price
 */
function optionLegProductSelection(
  state: Draft<State>,
  action: PayloadAction<LegProductSelectionPayload>,
) {
  const { productCode, legIndex } = action.payload;
  const product = getProduct(productCode);
  if (product.kind !== 'OPTION') {
    throw new TypeError('Product code should be of kind OPTION.');
  }

  const currentLeg = selectLeg(state, legIndex);
  currentLeg.productCode = productCode;
  currentLeg.kind = 'OPTION';

  state.legs = state.legs.map((leg, index) => {
    if (leg.isHedge) {
      return {
        ...leg,
        ...generateHedgeLegFromOption(state, currentLeg as DraftOptionLeg),
        index,
      };
    }
    return {
      ...leg,
      index,
      productCode,
      kind: 'OPTION',
    } as DraftOptionLeg;
  });
}

/**
 * Multi-product (multi-underlying) leg product selection, used when
 * `feature_um_multi_product_rfq` allows the selected venue+auction to mix
 * products across strategy legs.
 *
 * Only the target leg is retargeted to the chosen product — sibling legs
 * (including hedge legs) are left untouched. The structure-level product is
 * then recomputed: a single-product result keeps today's account defaulting
 * and kind-change custom-strategy semantics; a mixed result is always a
 * custom strategy with no structure product (`productCode === null`).
 */
function multiProductLegProductSelection(
  state: Draft<State>,
  action: PayloadAction<LegProductSelectionPayload>,
) {
  const { productCode, legIndex, defaultAccounts } = action.payload;
  const product = getProduct(productCode);
  const currentLeg = selectLeg(state, legIndex);
  const isSameProduct = isSameRfqProduct(state, productCode);
  const kindChanged = currentLeg.kind !== product.kind;

  state.legs = state.legs.map((leg) =>
    leg.index === legIndex ? retargetLegProduct(state, leg, product) : leg,
  );

  const codes = rfqProductCodes(state.legs);
  if (codes.length === 1) {
    // Single-product structure (e.g. the odd leg was changed back): keep the
    // existing structure-product, account-defaulting and kind-change rules.
    state.productCode = codes[0]!;
    if (!isSameProduct) {
      state.account = getDefaultAccount(state, {
        productCode,
        venue: product.venue,
        defaultAccounts,
      });
    }
    if (kindChanged) applyCustomStrategy(state, state.productCode);
    return;
  }

  // Mixed structure: always a custom strategy without a structure product.
  // There is no single product to look a default account up for, so the
  // account is left unchanged.
  applyCustomStrategy(state, null);
}

/**
 * Retargets a single leg to the given product, clearing the fields the old
 * product's instrument ladder no longer applies to (expiration, strike).
 */
function retargetLegProduct(
  state: Draft<State>,
  leg: DraftLeg,
  product: Product,
): DraftLeg {
  if (product.kind === 'OPTION') {
    return {
      index: leg.index,
      productCode: product.code,
      kind: 'OPTION',
      action: leg.action,
      expiration: undefined,
      ratio: leg.ratio,
      quantity: leg.quantity,
      optionKind: isOptionLeg(leg) ? leg.optionKind : undefined,
      strike: undefined,
      isHedge: leg.isHedge,
    };
  }

  return {
    index: leg.index,
    productCode: product.code,
    kind: 'FUTURE',
    action: leg.action,
    expiration: soleFutureExpiration(state, product, leg.isHedge),
    ratio: leg.ratio,
    quantity: leg.quantity,
    isHedge: leg.isHedge,
  };
}

/**
 * The only selectable expiration of a future product, or undefined when
 * there are several (or none loaded yet). Saves a click on products with a
 * single-instrument ladder, e.g. Paradex futures, which are perpetual-only:
 * the expiration selector would offer exactly one option, so it is
 * pre-selected instead. Mirrors the selector's own filtering: products
 * whose perpetual is hedge-only (isPerpStrategyLegDisabled) exclude it on
 * strategy legs.
 */
function soleFutureExpiration(
  state: Draft<State>,
  product: Product,
  isHedge: boolean,
): number | null | undefined {
  const instruments = state.instruments.futureInstruments.filter(
    (ins) => ins.product_code === product.code,
  );
  const selectable =
    !isHedge && product.isPerpStrategyLegDisabled === true
      ? instruments.filter((ins) => ins.expiration != null)
      : instruments;
  const expirations = new Set(selectable.map((ins) => ins.expiration ?? null));
  if (expirations.size !== 1) return undefined;
  return [...expirations][0];
}

/**
 * A leg product selection only changes the RFQ product when it differs from
 * the current one. `state.productCode` may be `null` for custom (mixed)
 * strategies, so the categorized legs are checked as well — categorizing a
 * new leg with the product already in use keeps the RFQ product unchanged
 * and must not discard a manually selected account.
 */
function isSameRfqProduct(state: Draft<State>, productCode: string) {
  if (state.productCode === productCode) return true;
  const legProductCodes = state.legs
    .filter((leg) => !isHedgeLeg(leg))
    .map((leg) => leg.productCode)
    .filter((code): code is string => code != null);
  return (
    legProductCodes.length > 0 &&
    legProductCodes.every((code) => code === productCode)
  );
}

function getDefaultAccount(
  state: Draft<State>,
  options: DefaultAccountOptions,
) {
  const { account } = state;
  const { venue, productCode, defaultAccounts } = options;
  if (state.auction !== 'RFQ' || defaultAccounts == null) return account;
  const venueAccount = defaultAccounts[venue]?.all;
  if (productCode == null) return venueAccount;
  return defaultAccounts[venue]?.[productCode] ?? venueAccount ?? '';
}

function updateSingleOptionLegExpiration(
  state: Draft<State>,
  { expiration, legIndex }: UpdateSingleOptionLegExpirationPayload,
) {
  const leg = selectLeg(state, legIndex);
  assertIsOptionLeg(leg);
  leg.expiration = expiration;
  leg.strike = hasExpirationForStrike(state, expiration, leg.strike)
    ? leg.strike
    : undefined;
}

interface UpdateAllOptionLegsExpirationPayload {
  readonly expiration: Defined<DraftOptionLeg['expiration']>;
}

function updateAllOptionLegsExpiration(
  state: Draft<State>,
  { expiration }: UpdateAllOptionLegsExpirationPayload,
) {
  state.legs.filter(isOptionLeg).forEach((leg) => {
    leg.expiration = expiration;

    leg.strike = hasExpirationForStrike(state, expiration, leg.strike)
      ? leg.strike
      : undefined;
  });
}

function hasExpirationForStrike(
  state: Draft<State>,
  expiration: Defined<DraftOptionLeg['expiration']>,
  strike: Defined<DraftOptionLeg['strike']> | undefined,
) {
  return state.instruments.optionInstruments.some(
    (instrument) =>
      instrument.expiration === expiration && instrument.strike === strike,
  );
}

interface UpdateSingleOptionLegStrikePayload {
  readonly strike: Defined<DraftOptionLeg['strike']>;
  readonly legIndex: number;
}

function updateSingleOptionLegStrike(
  state: Draft<State>,
  { strike, legIndex }: UpdateSingleOptionLegStrikePayload,
) {
  const leg = selectLeg(state, legIndex);
  assertIsOptionLeg(leg);
  leg.strike = strike;
}

interface UpdateAllOptionLegsStrikePayload {
  readonly strike: Defined<DraftOptionLeg['strike']>;
}

function updateAllOptionLegsStrike(
  state: Draft<State>,
  { strike }: UpdateAllOptionLegsStrikePayload,
) {
  state.legs.filter(isOptionLeg).forEach((leg) => {
    leg.strike = strike;
  });
}

function updateLegsQuantityFromBaseLeg(state: Draft<State>, quantity: string) {
  state.legs.forEach((leg, index) => {
    if (index === 0) return;
    if (isHedgeLeg(leg)) return;
    if (quantity === '') {
      leg.quantity = '';
      return;
    }
    if (!isValidDecimal(quantity)) return;

    const ratio = isValidDecimal(leg.ratio)
      ? parseFormattedFloat(leg.ratio)
      : 1;

    leg.quantity = BigNumber(parseFormattedFloat(quantity))
      .multipliedBy(ratio)
      .toString();
  });
}

/**
 * Update ratio for a RFQ Auction.
 *
 * In a RFQ auction the user fills the quantity field and leg price field.
 * The ratio is calculated from these values.
 * - For a regular leg: It updates the current leg ratio with the following: ratio = leg.quantity / baseLeg.quantity
 * - For hedge leg: Apply other calculation, please see updateHedgeLegRatio function
 * - If the whole structure is a custom strategy: It finds the base leg and update all ratios accordingly
 *
 * This function should be called to update the ratio, whenever quantity and leg prices changes.
 */
function updateRfqDerivedRatio(state: Draft<State>) {
  if (state.auction !== 'RFQ') return;

  const baseLeg = selectBaseLeg(state);
  if (baseLeg == null || baseLeg.quantity == null) return;

  for (const leg of state.legs) {
    const strategyQty = calculateStrategyQty(state.legs);
    const ratio = calculateLegRatio(strategyQty, leg);
    if (ratio == null) continue;
    leg.ratio = ratio;
  }
}

// Finds a strategy that matches the current state ratios
function matchStrategyByRatio(state: Draft<State>) {
  const strategyLegs = state.legs.filter((leg) => !isHedgeLeg(leg));

  return Object.values(STRATEGIES).find((strategySpec) => {
    if (strategyLegs.length !== strategySpec.legs.length) return false;

    return [...strategySpec.legs].every((leg, index) => {
      const { ratio, action: side } = strategyLegs[index]!;
      const sideMultiplier = side === 'BUY' ? 1 : -1;
      return leg.ratio === Number(ratio) * sideMultiplier;
    });
  });
}

function selectLeg(state: Draft<State>, legIndex: number) {
  const leg = state.legs[legIndex];
  if (leg == null)
    throw new TypeError(`Leg with index ${legIndex} does not exist.`);
  return leg;
}

function selectBaseLeg(state: Draft<State>) {
  const index = findBaseLegIndex(state.legs);
  if (index === -1) return null;

  return state.legs[index];
}

export function customStrategySelection(state: Draft<State>) {
  state.legs = [];
  state.strategyCode = null;
}

export function predefinedStrategySelection(
  state: Draft<State>,
  strategyCode: string,
) {
  const strategy = STRATEGIES[strategyCode]!;

  if (state.productCode == null) {
    state.productCode =
      strategy.kind === 'FUTURE'
        ? getDefaultFutureProductCode(state.venue)
        : getDefaultProductCode(state.venue);
  }
  state.strategyCode = strategyCode;

  if (state.productCode == null)
    throw new TypeError('A product is needed to generate a strategy from');

  if (strategy.kind === 'FUTURE') {
    const product = getProduct(state.productCode);
    state.legs = strategy.legs
      .map(futureLegGenerator(state.productCode))
      .map((leg) => ({
        ...leg,
        expiration: soleFutureExpiration(state, product, leg.isHedge),
      }));
    return;
  }

  state.legs = strategy.legs.map(optionLegGenerator(state.productCode));
}

function assertIsOptionLeg(
  leg: DraftLeg,
): asserts leg is Draft<DraftOptionLeg> {
  if (!isOptionLeg(leg))
    throw new TypeError(
      `Leg with index ${leg.index} is supposed to be` +
        ` of kind 'OPTION', but it's '${leg.kind}'.`,
    );
}

function assertIsFutureLeg(
  leg: DraftLeg,
): asserts leg is Draft<DraftFutureLeg> {
  if (!isFutureLeg(leg))
    throw new TypeError(
      `Leg with index ${leg.index} is supposed to be` +
        ` of kind 'FUTURE', but it's '${leg.kind}'.`,
    );
}

function assertStrategyCode(
  strategyCode: State['strategyCode'],
): asserts strategyCode {
  if (strategyCode == null)
    throw new TypeError('Strategy code should be defined at this point.');
}

export function hedgeLegActionBySign(
  rfqQuantityOrObRatio: Maybe<BigNumber>,
  currentAction: 'BUY' | 'SELL' = 'BUY',
) {
  if (rfqQuantityOrObRatio == null) return currentAction;
  return rfqQuantityOrObRatio.isNegative() ? 'SELL' : 'BUY';
}

export function isFirstHedgeLeg(legs: readonly DraftLeg[], leg: DraftLeg) {
  const hedgeLegs = legs.filter(isHedgeLeg);
  const singleHedgeLeg = hedgeLegs.length === 1;
  return singleHedgeLeg || leg.index === hedgeLegs[0]?.index;
}

export const hasLoadedInstrumentsSelection = (
  state: Pick<State, 'instruments'>,
) => {
  return (
    state.instruments.futureInstruments.length > 0 &&
    state.instruments.optionInstruments.length > 0
  );
};

export const greeksSelection = (
  state: Pick<State, 'legs' | 'auction' | 'instruments'>,
) => {
  return calculateGreeks(
    state.legs,
    state.auction,
    state.instruments.optionInstruments,
  );
};
